import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Wrench, 
  PaintBucket, 
  Building,
  Check,
  Phone,
  ChevronRight,
  Droplets,
  ThermometerSun,
  Filter,
  Gauge
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const services = [
  {
    id: 'residential',
    icon: Sparkles,
    title: 'Residential Pool Cleaning',
    subtitle: 'Weekly Professional Maintenance',
    description: 'Keep your backyard oasis crystal clear with our comprehensive weekly cleaning service. Our trained technicians handle everything so you can simply enjoy your pool.',
    image: '/images/016-5503-Harvest-Oak-Landing-Dr-Spring.jpg',
    link: '/services/residential',
    features: [
      'Weekly skimming and debris removal',
      'Chemical testing and balancing',
      'Filter cleaning and maintenance',
      'Equipment inspection'
    ],
    benefits: [
      { icon: Droplets, text: 'Crystal clear water' },
      { icon: ThermometerSun, text: 'Proper chemical balance' },
    ]
  },
  {
    id: 'repair',
    icon: Wrench,
    title: 'Pool Repair & Maintenance',
    subtitle: 'Expert Equipment Services',
    description: 'From minor repairs to major equipment replacement, our certified technicians have the expertise to keep your pool running smoothly.',
    image: '/images/45_Landry.jpg',
    link: '/services/repair-maintenance',
    features: [
      'Pump repair and replacement',
      'Filter system services',
      'Heater repair and installation',
      'Leak detection and repair'
    ],
    benefits: [
      { icon: Wrench, text: 'Expert diagnostics' },
      { icon: Check, text: 'Quality parts' },
    ]
  },
  {
    id: 'renovations',
    icon: PaintBucket,
    title: 'Pool Renovations',
    subtitle: 'Transform Your Pool',
    description: 'Give your pool a stunning makeover with our renovation services. From resurfacing to complete redesigns, we bring your vision to life.',
    image: '/images/Wolf_b-1.jpg',
    link: '/services/renovations',
    features: [
      'Pool resurfacing options',
      'Tile and coping replacement',
      'LED lighting upgrades',
      'Water feature installation'
    ],
    benefits: [
      { icon: Sparkles, text: 'Modern aesthetics' },
      { icon: Gauge, text: 'Increased value' },
    ]
  },
  {
    id: 'commercial',
    icon: Building,
    title: 'Commercial Pool Services',
    subtitle: 'Professional Business Solutions',
    description: 'We provide reliable, compliant pool services for hotels, apartments, HOAs, fitness centers, and other commercial properties.',
    image: '/images/467191288_560975559913547_5030626414160754671_n.jpg',
    link: '/services/commercial',
    features: [
      'Daily or weekly maintenance',
      'Health code compliance',
      'Chemical management systems',
      'Emergency response service'
    ],
    benefits: [
      { icon: Check, text: 'Health code compliant' },
      { icon: Wrench, text: '24/7 support' },
    ]
  },
];

export const Services = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation for service sections
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="services-page">
      <Helmet>
        <title>Pool Services | Cleaning, Repair, Renovation & Commercial | A-Quality Pool Service</title>
        <meta name="description" content="Professional pool services in Tampa Bay: weekly cleaning, equipment repair, pool renovation & resurfacing, and commercial pool management. Licensed & insured. Call 813-453-5988." />
        <link rel="canonical" href="https://a-qualitypools.net/services" />
      </Helmet>
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img loading="lazy" decoding="async"
            src="/images/016-5503-Harvest-Oak-Landing-Dr-Spring.jpg"
            alt=""
            width={1200}
            height={799}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-sky-400 font-medium mb-4 block">Our Services</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Professional Pool Services
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              From weekly maintenance to complete renovations, A-Quality Pool Service 
              delivers excellence in every drop.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`service-nav-${service.id}`}
              >
                <Link to={service.link}>
                  <Card className="h-full border-2 border-slate-100 hover:border-sky-500 transition-colors">
                    <CardContent className="p-6 text-center">
                      <div className="mx-auto w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-sky-500 transition-colors">
                        <service.icon className="h-7 w-7 text-sky-500 group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{service.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{service.subtitle}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Service Sections */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          data-testid={`service-section-${service.id}`}
          className={`py-24 ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-sky-500 rounded-xl mr-4">
                    <service.icon className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-sky-500 font-medium">{service.subtitle}</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  {service.title}
                </h2>
                
                <p className="text-lg text-slate-600 mb-8">
                  {service.description}
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    data-testid={`service-cta-${service.id}`}
                    className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-6"
                  >
                    <Link to={service.link}>
                      Learn More
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-6"
                  >
                    <a href="tel:813-453-5988">
                      <Phone className="mr-2 h-4 w-4" />
                      Call Now
                    </a>
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                animate={{ opacity: 1, x: 0 }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden shadow-2xl">
                    <img loading="lazy" decoding="async"
                      src={service.image}
                      alt={service.title}
                      width={1200}
                      height={800}
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                  
                  {/* Benefits badges */}
                  <div className="absolute -bottom-8 left-8 right-8">
                    <Card className="shadow-xl">
                      <CardContent className="p-6">
                        <div className="grid grid-cols-2 gap-4">
                          {service.benefits.map((benefit, i) => (
                            <div key={i} className="flex items-center">
                              <benefit.icon className="h-5 w-5 text-sky-500 mr-2" />
                              <span className="text-sm text-slate-700">{benefit.text}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="pt-20 pb-36 bg-sky-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Not Sure What You Need?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Contact us for a free consultation. We'll assess your pool and recommend 
              the best services for your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                data-testid="services-cta-quote"
                className="bg-white text-sky-600 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/contact">Get Free Consultation</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-sky-600 rounded-full px-8 py-6 text-lg bg-transparent"
              >
                <a href="tel:813-453-5988">
                  <Phone className="mr-2 h-5 w-5" />
                  813-453-5988
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
